/**
 * レシピ文書から取り込んだ仕込みの作り方(#250)を直す(#259)。
 *
 * 取り込みは文書に書いてあることを写すだけなので、同じ頁の別の作り方(「揚げ豚ガツ」の頁の
 * 揚げる手順など)まで入ることがある。料理長が材料・手順・補足をその場で直せるようにする。
 *
 * 記録は「マニュアルを編集」(#203)と同じ manualEdits(target=prerequisite, field=procedure,
 * value=JSON)。本文へは「回答を本文に反映」で resolve が当てる(画面は依頼を記録するだけ)。
 * 本文と同じ内容に戻して保存したら記録を消す(差分だけを持つ)。
 *
 * creating のときは、作り方が未作成の仕込みに料理長が一から書く(資料に作り方が載っていない
 * 仕込み)。記録の形は同じで、反映すると「料理長が記入」の作り方として載る。
 */
import { useState } from "react";
import { deleteDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import {
  editDocId,
  parsePrepProcedure,
  serializePrepProcedure,
  type EditDoc,
  type PrepProcedure,
} from "../lib/manual-edits";

interface Props {
  dishId: string;
  /** 仕込みの名前(本文の prerequisites[].name) */
  name: string;
  /** 本文の作り方(反映前の値) */
  current: PrepProcedure;
  /** 記録済みの編集(manualEdits 全件) */
  edits: Record<string, EditDoc>;
  /** 未作成の仕込みに一から書く(current は空) */
  creating?: boolean;
  onClose: () => void;
}

export function PrepProcedureEditPanel({ dishId, name, current, edits, creating, onClose }: Props) {
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const id = editDocId("prerequisite", name, "procedure");
  const recorded = edits[id] ? parsePrepProcedure(edits[id].value) : null;
  const start = recorded ?? current;
  const [rows, setRows] = useState(() => {
    const r = start.ingredients.map((i) => ({ name: i.name, amount: i.amount ?? "" }));
    // 一から書くときは空の行を1つ出しておく(「行を追加」を押させない)
    return r.length === 0 && creating ? [{ name: "", amount: "" }] : r;
  });
  const [steps, setSteps] = useState(start.steps.join("\n"));
  const [notes, setNotes] = useState(start.notes.join("\n"));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lines = (v: string) => v.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  const draft: PrepProcedure = {
    // 名前の無い行(出しておいた空行)は材料に数えない
    ingredients: rows.filter((r) => r.name.trim()).map((r) => ({ name: r.name, amount: r.amount || null })),
    steps: lines(steps),
    notes: lines(notes),
  };
  const empty = draft.steps.length === 0;

  async function save() {
    if (!user || !paths || empty) return;
    setSaving(true);
    setError(null);
    try {
      const value = serializePrepProcedure(draft);
      const ref = paths.manualEditDoc(dishId, id);
      // 本文と同じに戻したら記録そのものを消す(差分だけを持つ。#203 と同じ)
      if (value === serializePrepProcedure(current)) await deleteDoc(ref);
      else
        await setDoc(ref, {
          target: "prerequisite",
          key: name,
          field: "procedure",
          value,
          editedBy: user.uid,
          editedAt: serverTimestamp(),
        });
      onClose();
    } catch (e) {
      setError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  async function revert() {
    if (!paths) return;
    setSaving(true);
    try {
      await deleteDoc(paths.manualEditDoc(dishId, id));
      onClose();
    } catch (e) {
      setError(`${t("取り消しに失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card step-edit prep-proc-edit">
      <p className="muted small">
        {creating
          ? t("「{name}」の作り方を書いてください。保存した内容は「回答を本文に反映」でマニュアルに入ります。", { name })
          : t("「{name}」の作り方を直します。保存した内容は「回答を本文に反映」でマニュアルに入ります。", { name })}
      </p>
      <div className="edit-field">
        <label>{t("材料と分量")}</label>
        {rows.map((r, i) => (
          <div className="prep-import-row" key={i}>
            <input
              type="text"
              value={r.name}
              placeholder={t("材料名")}
              onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            />
            <input
              type="text"
              value={r.amount}
              placeholder={t("分量（例: 500g）")}
              onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
            />
            <button className="link" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
              {t("この行を削除")}
            </button>
          </div>
        ))}
        <button className="link" onClick={() => setRows((rs) => [...rs, { name: "", amount: "" }])}>
          {t("材料の行を追加")}
        </button>
      </div>
      <div className="edit-field">
        <label>{t("手順（1行に1つ）")}</label>
        <textarea rows={Math.max(4, draft.steps.length + 1)} value={steps} onChange={(e) => setSteps(e.target.value)} />
        {empty && <p className="error small">{t("手順を1つ以上書いてください")}</p>}
      </div>
      <div className="edit-field">
        <label>{t("補足（保存方法・出典など。1行に1つ）")}</label>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {error && <p className="error">{error}</p>}
      <div className="step-edit-actions">
        <button disabled={saving || empty} onClick={() => void save()}>
          {saving ? t("保存中…") : t("保存")}
        </button>
        {recorded && (
          <button className="link" disabled={saving} onClick={() => void revert()}>
            {t("修正を取り消す")}
          </button>
        )}
        <button className="link" onClick={onClose} disabled={saving}>
          {t("閉じる")}
        </button>
      </div>
    </div>
  );
}
